
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface ServiceDetailPricingProps {
  data: {
    title: string;
    packages: Array<{
      name: string;
      price: string;
      period: string;
      description: string;
      features: string[];
      highlighted: boolean;
    }>;
  };
}

const ServiceDetailPricing = ({ data }: ServiceDetailPricingProps) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const scrollToContact = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  return (
    <section ref={ref} className="py-16 bg-white">
      <div className="max-w-[1440px] mx-auto px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className="font-poppins font-bold text-2xl lg:text-3xl text-gray-900 mb-3">
            {data.title}
          </h2>
          <p className="font-poppins text-base text-gray-600">
            Size en uygun paketi seçin veya özel çözüm için iletişime geçin
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {data.packages.map((pkg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`relative bg-white rounded-2xl p-6 transition-all duration-300 cursor-pointer ${
                pkg.highlighted
                  ? 'border-2 border-[#007dff] shadow-xl scale-105'
                  : 'border border-gray-200 shadow-sm hover:shadow-lg'
              }`}
            >
              {/* Popular Badge */}
              {pkg.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#007dff] to-[#0066dd] text-white px-4 py-1 rounded-full">
                  <p className="font-poppins text-[11px] font-bold uppercase tracking-wide">En Popüler</p>
                </div>
              )}

              {/* Package Name */}
              <h3 className="font-poppins font-bold text-lg text-gray-900 mb-2">
                {pkg.name}
              </h3>
              <p className="font-poppins text-[12px] text-gray-600 mb-4">
                {pkg.description}
              </p>

              {/* Price */}
              <div className="mb-5">
                <div className="flex items-baseline gap-1">
                  <span className="font-poppins font-bold text-3xl text-gray-900">
                    ₺{pkg.price}
                  </span>
                  <span className="font-poppins text-[13px] text-gray-600">
                    / {pkg.period}
                  </span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <i className="ri-check-line text-base text-[#007dff] flex-shrink-0 mt-0.5"></i>
                    <span className="font-poppins text-[13px] text-gray-700">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={scrollToContact}
                className={`w-full py-2.5 rounded-xl font-poppins text-[14px] font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  pkg.highlighted
                    ? 'bg-gradient-to-r from-[#007dff] to-[#0066dd] text-white hover:shadow-lg hover:shadow-[#007dff]/30'
                    : 'bg-gray-100 text-gray-900 hover:bg-[#007dff] hover:text-white'
                }`}
              >
                Hemen Başla
              </button>
            </motion.div>
          ))}
        </div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-8"
        >
          <p className="font-poppins text-[13px] text-gray-600">
            Özel ihtiyaçlarınız için{' '}
            <button
              onClick={scrollToContact}
              className="text-[#007dff] font-semibold hover:underline cursor-pointer"
            >
              bizimle iletişime geçin
            </button>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceDetailPricing;
